<template>
  <div class="flex flex-wrap items-center gap-4">
    <RevTag
      v-if="shouldDisplayStatusTag"
      class="truncate"
      data-test="order-details-status"
      :label="statusTagLabel"
      :size="isSmallScreen ? 'small' : 'medium'"
      :variant="statusTagVariant"
    />
    <RevTag
      v-if="shouldDisplayReturnStatusTag"
      class="truncate"
      data-test="order-details-return-status"
      :label="returnStatusTagLabel"
      :size="isSmallScreen ? 'small' : 'medium'"
      variant="info"
    />
    <RevTag
      v-if="orderline.partnership"
      class="truncate"
      data-test="order-details-partnership"
      :label="i18n(translations.replacementOrder)"
      :size="isSmallScreen ? 'small' : 'medium'"
      variant="warning"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevTag } from '@ds/components/Tag'
import { useMediaQuery } from '@vueuse/core'

import { ORDERLINE_STATES } from '~/api/care-folders/types/orderline-states.types'
import type { Orderline } from '~/api/customer-request/types'
import type { ProductReturn } from '~/api/seller-after-sales/types'

import { isOneWay } from '../../../Main/components/Details/components/Returns/ProductReturn/ProductReturn.utils'

import translations from './StatusTags.translations'

const i18n = useI18n()

const { orderline, productReturn } = defineProps<{
  orderline: Orderline
  productReturn: ProductReturn | null
}>()

const isOrderlineRefunded = computed(
  () =>
    orderline.state === 'REFUNDED_POST_DELIVERY' ||
    orderline.state === 'REFUNDED_PRE_DELIVERY',
)

const shouldDisplayReturnStatusTag = computed(() => {
  return productReturn && !isOrderlineRefunded.value
})

const shouldDisplayStatusTag = computed(() => {
  if (Object.values(ORDERLINE_STATES).includes(orderline.state)) {
    return (
      !shouldDisplayReturnStatusTag.value &&
      orderline.state !== 'NEW' &&
      orderline.state !== 'NOT_PAID'
    )
  }

  return false
})
const statusTagLabel = computed(() => {
  if (isOrderlineRefunded.value) {
    return i18n(translations.refunded)
  }

  switch (orderline.state) {
    case 'CANCELLED':
      return i18n(translations.cancelled)

    case 'SHIPPED':
      return i18n(translations.shipped)

    case 'VALIDATED':
      return i18n(translations.validated)

    case 'PAID':
      return i18n(translations.paid)

    default:
      return ''
  }
})

const statusTagVariant = computed(() => {
  if (isOrderlineRefunded.value && orderline.state === 'CANCELLED') {
    return 'danger'
  }

  return 'secondary'
})

const returnStatusTagLabel = computed(() => {
  if (!productReturn) return ''

  if (productReturn.dateDelivered) {
    return isOneWay(productReturn)
      ? i18n(translations.deliveredToTheSeller)
      : i18n(translations.returnProductReturnDelivered)
  }

  if (productReturn.datePickedUp) {
    return isOneWay(productReturn)
      ? i18n(translations.productReturnPickedUp)
      : i18n(translations.returnProductReturnPickedUp)
  }

  if (productReturn.creationDate && !isOneWay(productReturn)) {
    return i18n(translations.returnProductReturnCreated)
  }

  return ''
})

const isSmallScreen = useMediaQuery('(max-width: 768px)', { ssrWidth: 768 })
</script>
